import React from 'react'
import { Grid as BaseGrid, Hidden as BaseHidden, styled } from '@mui/material'

import { Close as BaseCloseIcon, Search as BaseSearchIcon } from '@mui/icons-material'
import { Spinner, InputPrefix } from '@chilecompra/react-kit/components'

export const Root = styled('div')`
  box-sizing: border-box;
  padding: 0 0 130px;
  width: 100%;
`

export const Section = styled(({ margin, maxWidth, ...props }) => <BaseGrid {...props} />)`
  max-width: ${props => props.maxWidth || 'initial'};

  &.MuiGrid-item {
    margin: ${props => props.margin || 'initial'};
  }
`

export const Strong = styled('span')`
  font-weight: bold;
`

export const PhoneNumberPrefix = styled(InputPrefix)`
  padding: 0.375rem 0.75rem;
`

export const Title = styled('h1')`
  color: ${props => props.theme.palette.brand.black1};
  font-family: ${props => props.theme.typography.fontFamily};
  font-feature-settings:
    'clig' off,
    'liga' off;
  font-size: 27px;
  font-style: normal;
  font-weight: 700;
  line-height: 31px;

  ${props => props.theme.breakpoints.down('sm')} {
    font-size: 22px;
    line-height: 26px;
    margin: 0;
    text-align: left;
  }
`

export const Instructions = styled('p')`
  color: ${props => props.theme.palette.brand.black1};
  font-family: ${props => props.theme.typography.fontFamily};
  font-feature-settings:
    'clig' off,
    'liga' off;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 21px;
  margin: 0;

  ${props => props.theme.breakpoints.down('sm')} {
    text-align: left;
  }
`

export const Form = styled('form')`
  width: 100%;
  ${props => props.theme.breakpoints.down('sm')} {
    margin-top: -15px;
  }
  label {
    color: ${props => props.theme.palette.brand.black1};
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 18px;
    text-align: left;
  }
`

export const LinkHelp = styled('a')`
  align-items: center;
  color: ${props => props.theme.palette.blue[50]};
  cursor: pointer;
  font-weight: 400;
  font-size: 14px;
  text-decoration: none;
  pointer-events: visible;

  &:hover {
    color: ${props => props.theme.palette.blue[35]};
    text-decoration: underline;
  }
`

export const Search = styled(({ clear, loading, ...props }) => <button {...props} />)`
  align-items: center;
  background-color: ${props => (props.clear ? props.theme.palette.brand.black3 : props.theme.palette.brand.primary)};
  border: 0;
  border-radius: 0;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  height: 100%;
  justify-content: center;
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  outline: 0;
  pointer-events: ${props => (props.disabled ? 'none' : 'initial')};
  user-select: ${props => (props.disabled ? 'none' : 'initial')};
  width: 40px;

  &:focus {
    outline: 0;
  }

  &:hover {
    background-color: ${props => (props.clear ? props.theme.palette.gray[35] : props.theme.palette.blue[35])};
  }
`

export const SearchClearIcon = styled(BaseCloseIcon)`
  color: ${props => props.theme.palette.brand.white};
  height: 24px;
  width: 24px;
`

export const SearchIcon = styled(BaseSearchIcon)`
  color: ${props => props.theme.palette.brand.white};
  height: 24px;
  width: 24px;
`

export const SearchLoadingIcon = styled(props => <Spinner {...props} size="18px" />)`
  color: ${props => props.theme.palette.brand.white};
  & .MuiCircularProgress-root {
    color: ${props => props.theme.palette.brand.white};
  }
`

export const TaxesKeyInstructions = styled('p')`
  color: ${props => props.theme.palette.custom.charcoal};
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 20px;
  margin: 0;
  margin-bottom: 20px;
  text-align: left;
`

export const TaxesKey = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 422px;

  button {
    background-color: ${props => props.theme.palette.custom.darkBlue} !important;
  }

  ${props => props.theme.breakpoints.between('sm', 'lg')} {
    align-items: flex-start;
  }
`

export const TaxesKeyButton = styled('button')`
  border: 0;
  cursor: pointer;
  display: block;
  width: 200px;

  ${props => props.theme.breakpoints.only('xs')} {
    margin: 0 auto;
  }
`

export const TaxesKeyHelp = styled('div')`
  margin: 16px 0 0;
  text-align: center;
  width: 200px;

  ${props => props.theme.breakpoints.only('xs')} {
    margin: 16px auto 0;
  }
`

export const TaxesKeyHelpLink = styled('a')`
  color: ${props => props.theme.palette.brand.primary};
  cursor: pointer;
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 13px;
  letter-spacing: 0;
`

export const ActivitiesLink = styled('a')`
  color: ${props => props.theme.palette.brand.primary};
  cursor: pointer;
  font-weight: medium;
  letter-spacing: 0;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

export const Image = styled('img')`
  margin-top: -80px;
  width: 100%;
`

export const ImageContainer = styled('div')`
  align-items: center;
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin: -20px 0 15px 0;

  ${props => props.theme.breakpoints.between('xs', 'lg')} {
    margin: 0;
  }
`

export const ImageSuccess = styled('img')`
  width: 130px;
  margin-top: 45px;
  margin-bottom: 45px;
`

export const ToastContainer = styled('div')`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-left: 50px;
  margin-top: 15px;

  ${props => props.theme.breakpoints.between('xs', 'lg')} {
    margin-top: 20px;
    margin-left: 0;
    width: 100%;
  }

  & span {
    font-feature-settings:
      'liga' off,
      'clig' off;
    font-family: ${props => props.theme.typography.fontFamily};
    font-size: 14px;
    font-style: normal;
    line-height: 18px;
    color: ${props => props.theme.palette.brand.black1};
  }
`

export const ToastFontContainer = styled('div')`
  & span {
    font-feature-settings:
      'liga' off,
      'clig' off;
    font-family: ${props => props.theme.typography.fontFamily};
    font-size: 14px;
    font-style: normal;
    line-height: 18px;
    color: ${props => props.theme.palette.brand.black1};
  }
`

export const Divider = styled('div')`
  margin-bottom: 15px;
`

export const Hidden = styled(BaseHidden)``

export const Conditions = styled('div')`
  align-items: center;
  background: rgba(0, 100, 255, 0.03);
  border-radius: 4px;
  border: 1px solid ${props => props.theme.palette.brand.primary};
  color: ${props => props.theme.palette.brand.black1};
  display: flex;
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  margin-top: 20px;

  padding: 14px 14px;
  width: 90%;

  ${props => props.theme.breakpoints.down('sm')} {
    text-align: center;
  }
`

export const TermsLink = styled('a')``

export const RightSection = styled('div')`
  display: flex;
  flex-direction: column;
  width: 480px;
`
